import React, {useEffect} from 'react'
import {Control, FieldErrors, UseFormRegister, UseFormSetValue, UseFormWatch} from 'react-hook-form'
import {IApplicationFormData} from '../../models/application-form'
import {CheckboxInput} from '../../../common/component/CheckboxInput'
import {TextInput} from '../../../common/component/TextInput'
import RepeatableEntryList from './sections/RepeatableEntryList'
import {createEmptyPet, createEmptyVehicle} from './sections/petVehicleDraft'
import {petPath, vehiclePath} from './sections/petVehiclePath'
import WizardSectionCard from './sections/WizardSectionCard'
import YesNoChoice from './sections/YesNoChoice'
import {RadioButtonInput} from '../../../common/component/RadioButtonInput'
import {toOptionalBoolean} from '../wizard/wizardAccess'

interface PetsVehiclesStepProps {
  register: UseFormRegister<IApplicationFormData>
  control: Control<IApplicationFormData>
  errors?: FieldErrors<IApplicationFormData>
  setValue: UseFormSetValue<IApplicationFormData>
  watch: UseFormWatch<IApplicationFormData>
  triggerValidation?: () => Promise<boolean>
}

const PetsVehiclesStep: React.FC<PetsVehiclesStepProps> = ({
  register,
  control,
  errors,
  setValue,
  watch,
  triggerValidation,
}) => {
  const pets = watch('pets') ?? []
  const vehicles = watch('vehicles') ?? []
  const hasPets = toOptionalBoolean(watch('hasPets'))
  const hasVehicles = toOptionalBoolean(watch('hasVehicles'))

  const hasPetOptions = [
    {label: 'No', value: false},
    {label: 'Yes', value: true},
  ]

  const hasVehicleOptions = [
    {label: 'No', value: false},
    {label: 'Yes', value: true},
  ]

  const setPetsState = (enabled: boolean) => {
    const nextList = enabled ? (pets.length > 0 ? pets : [createEmptyPet()]) : []
    setValue('pets', nextList, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
    if (triggerValidation) triggerValidation()
  }

  const setVehiclesState = (enabled: boolean) => {
    const nextList = enabled ? (vehicles.length > 0 ? vehicles : [createEmptyVehicle()]) : []
    setValue('vehicles', nextList, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  const addPet = () => {
    setValue('pets', [...pets, createEmptyPet()], {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    })
  }

  const removePet = (index: number) => {
    const nextList = pets.filter((_, petIndex) => petIndex !== index)
    setValue('pets', nextList, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  const addVehicle = () => {
    setValue('vehicles', [...vehicles, createEmptyVehicle()], {
      shouldDirty: true,
      shouldTouch: false,
      shouldValidate: false,
    })
  }

  const removeVehicle = (index: number) => {
    const nextList = vehicles.filter((_, vehicleIndex) => vehicleIndex !== index)
    setValue('vehicles', nextList, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    })
  }

  useEffect(() => {
    if (triggerValidation) triggerValidation()
  }, [])

  return (
    <div>
      <div className='wizard-hint-card mb-4 d-flex ' style={{padding: '20px'}}>
        <i className='bi bi-info-circle me-5 gradient-icon'></i>
        <span>
          <strong>Bringing anything?</strong> List any pets and vehicles you'll bring with you.
        </span>
      </div>

      <div className='d-grid gap-4'>
        <WizardSectionCard title='Do you have pets? *' className='p-8'>
          <RadioButtonInput<IApplicationFormData>
            name='hasPets'
            label=''
            className='mt-5'
            control={control}
            errors={errors}
            required
            options={hasPetOptions}
            value={hasPets}
            defaultValue={hasPets}
            onChange={setPetsState}
          />

          {hasPets && (
            <>
              <RepeatableEntryList
                items={pets}
                itemTitle='Pet'
                addButtonText='Add Another Pet'
                onAdd={addPet}
                onRemove={removePet}
                renderItemFields={(index) => (
                  <>
                    <TextInput
                      name={petPath(index, 'type')}
                      label='Type'
                      placeholder='Type (e.g. Dog, Cat)'
                      register={register}
                      errors={errors}
                      required={true}
                    />

                    <TextInput
                      name={petPath(index, 'breed')}
                      label='Breed'
                      placeholder='e.g. Labrador, medium'
                      register={register}
                      errors={errors}
                      required={true}
                    />

                    <CheckboxInput
                      name={petPath(index, 'isAssistanceAnimal')}
                      label='Assistance animal'
                      register={register}
                      errors={errors}
                    />
                  </>
                )}
              />
            </>
          )}
        </WizardSectionCard>

        <WizardSectionCard title='Do you have a vehicle? *' className='p-8'>
          <RadioButtonInput<IApplicationFormData>
            name='hasVehicles'
            label=''
            className='mt-5'
            control={control}
            errors={errors}
            required
            options={hasVehicleOptions}
            value={hasVehicles}
            defaultValue={hasVehicles}
            onChange={setVehiclesState}
          />

          {hasVehicles && (
            <RepeatableEntryList
              items={vehicles}
              itemTitle='Vehicle Details'
              addButtonText='Add Vehicle'
              onAdd={addVehicle}
              onRemove={removeVehicle}
              renderItemFields={(index) => (
                <>
                  <TextInput
                    name={vehiclePath(index, 'registrationNumber')}
                    label='Rego plate number'
                    placeholder='e.g. ABC123'
                    register={register}
                    errors={errors}
                  />
                </>
              )}
            />
          )}
        </WizardSectionCard>
      </div>
    </div>
  )
}

export default PetsVehiclesStep
